import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useMobile } from './MobileContext';
import { Bell, BellRing, Share, Settings } from 'lucide-react';

const b64ToU8 = (s) => { const p = '='.repeat((4 - (s.length % 4)) % 4); const b = atob((s + p).replace(/-/g, '+').replace(/_/g, '/')); return Uint8Array.from([...b].map(c => c.charCodeAt(0))); };
const supported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export const MPushCard = ({ compact }) => {
  const { API, headers, t, isIOS, isStandalone, guestId, user } = useMobile();
  const [state, setState] = useState('unknown'); // off | on | denied | unsupported
  const [busy, setBusy] = useState(false);
  const ready = !!guestId || !!user;
  useEffect(() => {
    if (!ready) return;
    if (!supported()) { setState('unsupported'); return; }
    if (Notification.permission === 'denied') { setState('denied'); return; }
    navigator.serviceWorker.register('/sw.js').then(r => r.pushManager.getSubscription()).then(async (sub) => {
      if (!sub) { setState('off'); return; }
      await axios.post(`${API}/m/push/subscribe`, { subscription: sub.toJSON(), ua: navigator.userAgent }, { headers: headers() }).catch(() => {});
      setState('on');
    }).catch(() => setState('off'));
  }, [ready, user?.id]);
  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setState(perm === 'denied' ? 'denied' : 'off'); setBusy(false); return; }
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      const { data } = await axios.get(`${API}/push/vapid-public`);
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToU8(data.key) });
      await axios.post(`${API}/m/push/subscribe`, { subscription: sub.toJSON(), ua: navigator.userAgent }, { headers: headers() });
      setState('on'); toast.success(t('تم تفعيل الإشعارات ✅', 'Notifications enabled'));
    } catch { toast.error(t('تعذّر تفعيل الإشعارات', 'Could not enable notifications')); }
    setBusy(false);
  };
  if (!ready || state === 'unknown' || state === 'unsupported') return null;
  if (state === 'on') return compact ? null : <p className="mx-3 mt-2 text-[10px] text-emerald-700 flex items-center justify-center gap-1" data-testid="m-push-on"><BellRing className="w-3 h-3" />{t('الإشعارات مفعّلة — ستصلك ردودنا وتحديثات طلباتك', 'Notifications on')}</p>;
  const needInstall = isIOS && !isStandalone;
  const denied = state === 'denied';
  return (
    <div className={`mx-3 mt-3 rounded-2xl border p-3 flex items-center gap-3 ${denied ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-300 shadow-sm'}`} data-testid="m-push-card">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${denied ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700 animate-pulse'}`}>{denied ? <Settings className="w-5 h-5" /> : <Bell className="w-5 h-5" />}</div>
      <div className="flex-1 min-w-0 text-xs">
        <p className="font-bold text-slate-800">{denied ? t('الإشعارات محظورة', 'Notifications blocked') : t('فعّل الإشعارات (مهم)', 'Enable notifications (important)')}</p>
        {denied
          ? <p className="text-[11px] text-slate-600 leading-relaxed">{t('لن تصلك ردودنا وتحديثات طلباتك. افتح إعدادات المتصفح/الهاتف ← الإشعارات ← اسمح لهذا الموقع ثم أعد تحميل الصفحة.', 'You will miss our replies and updates. Open browser/phone settings → Notifications → allow this site, then reload.')}</p>
          : needInstall
            ? <p className="text-[11px] text-slate-600 leading-relaxed">{t('على آيفون: اضغط', 'On iPhone: tap')} <Share className="w-3 h-3 inline mx-0.5" /> {t('ثم "إضافة إلى الشاشة الرئيسية" وافتح التطبيق من الأيقونة لتفعيلها', 'then "Add to Home Screen" and open the app from the icon')}</p>
            : <p className="text-[11px] text-slate-600">{t('لتصلك ردودنا في الشات وتحديثات طلباتك فوراً حتى والتطبيق مغلق', 'Get chat replies and request updates instantly, even when the app is closed')}</p>}
      </div>
      {!needInstall && !denied && <button onClick={enable} disabled={busy} className="h-10 px-4 rounded-xl bg-[#0F172A] text-[#D4AF37] text-xs font-bold shrink-0" data-testid="m-push-enable">{t('تفعيل الآن', 'Enable now')}</button>}
    </div>
  );
};
